import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Lock,
  ShieldCheck,
  MapPin,
  Clock,
  CalendarDays,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { createCheckout } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { getInitials, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isClient } = useAuth();
  const state = location.state;

  const [redirecting, setRedirecting] = useState(false);
  const [initError, setInitError] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !isClient) {
      navigate('/login');
      return;
    }
    if (!state || !state.bookingSessionId) {
      setInitError('Missing booking details. Please choose a session again.');
    }
  }, [isAuthenticated, isClient, navigate, state]);

  const handleAuthorize = useCallback(async () => {
    if (!state?.bookingSessionId) return;
    setRedirecting(true);
    try {
      const res = await createCheckout(state.bookingSessionId);
      const url = res?.data?.checkout_url;
      if (!url) {
        throw new Error('Missing checkout_url from server');
      }
      // Redirect to Stripe hosted checkout
      window.location.href = url;
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Could not start checkout';
      toast.error(typeof msg === 'string' ? msg : 'Could not start checkout');
      setRedirecting(false);
    }
  }, [state]);

  if (initError) {
    return (
      <div className="min-h-screen bg-[#09090B] py-12 px-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-[#18181B] border border-zinc-800 rounded-lg p-8 text-center">
          <p className="text-zinc-200 mb-6" data-testid="checkout-error">{initError}</p>
          <Button
            onClick={() => navigate('/trainers')}
            className="btn-primary"
            data-testid="back-to-trainers-btn"
          >
            Back to trainers
          </Button>
        </div>
      </div>
    );
  }

  if (!state) return null;

  const amount = Number(state.amount || state.price || 0);
  const platformFee = Number(state.platform_fee || 0);

  return (
    <div className="min-h-screen bg-[#09090B] py-8 px-4 sm:px-6 lg:px-8" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-zinc-400 hover:text-white"
          data-testid="checkout-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Summary */}
          <aside className="lg:col-span-2 space-y-4">
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="checkout-summary">
              <h2 className="text-sm uppercase tracking-wider text-zinc-500 font-semibold mb-4">Booking summary</h2>

              <div className="flex items-center gap-3 mb-5 pb-5 border-b border-zinc-800">
                <Avatar className="w-12 h-12 border border-zinc-700">
                  <AvatarImage src={state?.trainerImage} alt={state?.trainerName} />
                  <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-sm font-semibold">
                    {getInitials(state?.trainerName || 'T')}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-white font-semibold">{state?.trainerName}</p>
                  <p className="text-xs text-zinc-400">{state?.categoryLabel}</p>
                </div>
              </div>

              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2 text-zinc-300">
                  <CalendarDays className="w-4 h-4 text-[#C0A062] mt-0.5 shrink-0" />
                  <span>{state?.scheduled_date}</span>
                </li>
                <li className="flex items-start gap-2 text-zinc-300">
                  <Clock className="w-4 h-4 text-[#C0A062] mt-0.5 shrink-0" />
                  <span>{state?.scheduled_time}</span>
                </li>
                {state?.location_address && (
                  <li className="flex items-start gap-2 text-zinc-300">
                    <MapPin className="w-4 h-4 text-[#C0A062] mt-0.5 shrink-0" />
                    <span className="break-words">{state.location_address}</span>
                  </li>
                )}
              </ul>

              <div className="mt-6 pt-5 border-t border-zinc-800 space-y-2">
                <div className="flex items-center justify-between text-sm text-zinc-400">
                  <span>Session</span>
                  <span>{formatCurrency(amount)}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-zinc-500">
                  <span>Platform fee (incl.)</span>
                  <span>{formatCurrency(platformFee)}</span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
                  <span className="text-white font-semibold">Total today</span>
                  <span className="text-[#C0A062] text-xl font-bold" data-testid="checkout-total">
                    {formatCurrency(amount)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-[#18181B] border border-emerald-500/20 rounded-lg p-4 text-xs text-zinc-300 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
              <span>You&apos;re only charged when payment succeeds. You can cancel up to 24h before your session.</span>
            </div>
          </aside>

          {/* Payment */}
          <section className="lg:col-span-3">
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6">
              <h1 className="text-2xl font-bold text-white mb-1">Payment</h1>
              <p className="text-sm text-zinc-400 mb-6">
                You&apos;ll be redirected to Stripe&apos;s secure checkout to complete your payment.
              </p>

              <Button
                type="button"
                onClick={handleAuthorize}
                disabled={redirecting}
                className="w-full btn-primary py-6 text-base"
                data-testid="pay-now-btn"
              >
                {redirecting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Redirecting to Stripe…
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Authorize {formatCurrency(amount)} &amp; send request
                  </>
                )}
              </Button>

              <p className="text-xs text-zinc-500 text-center mt-4 flex items-center justify-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                Payments processed by Stripe. We never store your card details.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
